import { alignSpeechWordsToPageText } from './read-along.js';

const ARABIC = /\p{Script_Extensions=Arabic}/u;
const LETTER = /\p{Letter}/u;
const MAX_TOKENS = 4000;
const MAX_CHARACTERS = 80000;
const PAGE_TIMEOUT_MS = 10000;
const cache = new WeakMap();
const empty = words => words.map(() => null);
const abortError = () => Object.assign(new Error('Office text request superseded'), { name: 'AbortError' });
const normalized = value => String(value).normalize('NFKD').replace(/\p{M}/gu, '').replace(/[^\p{L}\p{N}]+/gu, '').toLowerCase();

function direction(tokens, language) {
  let arabic = 0, latin = 0;
  for (const token of tokens) for (const c of token.text) {
    if (LETTER.test(c)) { if (ARABIC.test(c)) arabic++; else latin++; }
  }
  if (!arabic) return 'ltr';
  if (!latin) return 'rtl';
  return language ? (language.startsWith('ar') ? 'rtl' : 'ltr') : arabic > latin ? 'rtl' : 'ltr';
}

function groupRows(tokens) {
  const rows = [];
  for (const token of [...tokens].sort((a, b) => a.top + a.bottom - b.top - b.bottom || a.left - b.left)) {
    const matches = rows.filter(row => Math.min(row.bottom, token.bottom) - Math.max(row.top, token.top)
      > Math.min(row.bottom - row.top, token.bottom - token.top) * 0.5);
    if (matches.length > 1) return null;
    let row = matches[0];
    if (!row) { row = { top: token.top, bottom: token.bottom, tokens: [] }; rows.push(row); }
    row.tokens.push(token);
  }
  return rows;
}

function orderRow(row, language) {
  const leftToRight = [...row.tokens].sort((a, b) => a.left - b.left);
  const runs = [];
  for (const token of leftToRight) {
    const rtl = ARABIC.test(token.text);
    const prior = runs.at(-1), previous = prior?.tokens.at(-1);
    if (prior && prior.rtl === rtl && token.left - previous.right < Math.max(24, 1.5 * (row.bottom - row.top))) prior.tokens.push(token);
    else runs.push({ rtl, tokens: [token] });
  }
  // Embedded Latin terms and numbers retain their internal LTR order even in
  // an Arabic line. The underlying Office text already has logical letters.
  for (const run of runs) if (run.rtl) run.tokens.reverse();
  if (direction(row.tokens, language) === 'rtl') runs.reverse();
  return runs.flatMap(run => run.tokens);
}

function orderedCandidates(rows, language) {
  const candidates = [rows.flatMap(row => orderRow(row, language))];
  const cuts = new Set();
  for (const row of rows) {
    const tokens = [...row.tokens].sort((a, b) => a.left - b.left);
    for (let i = 1; i < tokens.length; i++) {
      if (tokens[i].left - tokens[i - 1].right > Math.max(24, 2 * (row.bottom - row.top))) {
        cuts.add((tokens[i].left + tokens[i - 1].right) / 2);
      }
    }
  }
  // A column candidate requires a real gutter through at least two rows. A
  // spanning heading can precede the columns; overlapping or interleaved
  // layouts are not guessed. Row order remains a separate candidate.
  for (const cut of [...cuts].slice(0, 8)) {
    const paired = rows.filter(row => row.tokens.some(t => t.right < cut)
      && row.tokens.some(t => t.left > cut) && row.tokens.every(t => t.right < cut || t.left > cut));
    if (paired.length < 2) continue;
    const top = paired[0].top;
    const before = rows.filter(row => row.bottom <= top);
    const body = rows.filter(row => !before.includes(row));
    if (body.some(row => row.tokens.some(t => t.left <= cut && t.right >= cut))) continue;
    const left = groupRows(body.flatMap(row => row.tokens.filter(t => t.right < cut)));
    const right = groupRows(body.flatMap(row => row.tokens.filter(t => t.left > cut)));
    if (!left || !right || left.length < 2 || right.length < 2) continue;
    const columns = direction(body.flatMap(row => row.tokens), language) === 'rtl' ? [right, left] : [left, right];
    candidates.push([...before, ...columns.flat()].flatMap(row => orderRow(row, language)));
  }
  const seen = new Set();
  return candidates.filter(tokens => {
    const key = tokens.map(t => t.start).join(',');
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
}

// Shared source-only projection; PDF consumers must independently prove glyph orientation.
export function projectArabicGroups(words, language) {
  const encoder = new TextEncoder();
  const projected = [];
  let prior = null;
  words.forEach((word, group) => {
    const original = () => projected.push({ word, group });
    if ((language && !language.startsWith('ar')) || typeof word?.value !== 'string' || !ARABIC.test(word.value)) {
      prior = null; original(); return;
    }
    const bytes = encoder.encode(word.value);
    // Polly offsets refer to original UTF-8 source, never Office UTF-16 text.
    // Without a complete byte-span contract, preserve whole-group matching.
    if (!Number.isSafeInteger(word.start) || !Number.isSafeInteger(word.end) || word.start < 0
      || word.end - word.start !== bytes.length || !bytes.length) {
      prior = null; original(); return;
    }
    let covered = 0;
    if (prior && word.start < prior.end) {
      covered = Math.min(word.end, prior.end) - word.start;
      if (word.start < prior.start || word.alignmentGapBefore === true
        || !bytes.subarray(0, covered).every((b, i) => b === prior.bytes[word.start - prior.start + i])) {
        prior = null; original(); return;
      }
    }
    let byteOffset = 0, characterOffset = 0;
    const parts = [...word.value.matchAll(/\S+/gu)].map(part => {
      byteOffset += encoder.encode(word.value.slice(characterOffset, part.index)).length;
      const start = byteOffset;
      byteOffset += encoder.encode(part[0]).length;
      characterOffset = part.index + part[0].length;
      return { value: part[0], start, end: byteOffset };
    });
    // An overlapping mark can repeat a proven source prefix. Trim only entire
    // covered tokens; never infer a missing byte offset or split a source word.
    if (parts.some(part => part.start < covered && part.end > covered)) {
      prior = null; original(); return;
    }
    if (!prior || word.end > prior.end) prior = { start: word.start, end: word.end, bytes };
    const remaining = parts.filter(part => part.end > covered);
    if (!remaining.length) remaining.push({ value: '' });
    remaining.forEach((part, index) => {
      const { alignmentGapAfter, sourceGapAfter, ...leading } = word;
      const token = index === 0 ? { ...leading, value: part.value } : { value: part.value };
      if (index === remaining.length - 1) Object.assign(token, { alignmentGapAfter, sourceGapAfter });
      projected.push({ word: token, group });
    });
  });
  return projected.length <= MAX_TOKENS ? projected : words.map((word, group) => ({ word, group }));
}

function projectedRanges(pageText, words, tokens, projected) {
  let text = '';
  const positions = [];
  for (const token of tokens) {
    for (let index = token.start; index < token.end; index++) { text += pageText[index]; positions.push(index); }
    text += ' '; positions.push(null);
  }
  const groups = words.map(() => []);
  alignSpeechWordsToPageText(text, projected.map(p => p.word)).forEach((range, wordIndex) => {
    if (!range) return;
    const segments = [];
    for (const part of range.segments || [range]) for (let index = part.start; index < part.end; index++) {
      const original = positions[index];
      if (original === null) continue;
      if (!Number.isInteger(original)) return;
      const last = segments.at(-1);
      if (last?.end === original) last.end++;
      else segments.push({ start: original, end: original + 1 });
    }
    if (!segments.length) return;
    // The shared PDF aligner can reverse visual Arabic glyphs. Office already
    // exposes logical letters, so never turn a different spelling into a match.
    const originalValue = segments.map(s => pageText.slice(s.start, s.end)).join(' ');
    if (normalized(originalValue) !== normalized(projected[wordIndex].word.value)) return;
    groups[projected[wordIndex].group].push(...segments);
  });
  // Keep one output per original audio mark. A bad token leaves a precise gap
  // within its phrase; it does not change source spelling or audio timings.
  return groups.map(segments => !segments.length ? null
    : segments.length === 1 ? segments[0] : { ...segments[0], segments });
}

/** Office-only projection: leaves source text, narration and canvas unchanged. */
export function alignOfficeSpeechWordsToGeometry(pageText, words, geometry, { language } = {}) {
  if (!Array.isArray(words)) return [];
  if (typeof pageText !== 'string' || pageText.length > MAX_CHARACTERS || !Array.isArray(geometry)
    || !geometry.length || geometry.length > MAX_TOKENS) return empty(words);
  const tokens = [...pageText.matchAll(/\S+/gu)];
  if (geometry.length !== tokens.length) return empty(words);
  const byStart = new Map();
  for (const token of geometry) {
    if (!Number.isInteger(token?.start) || !Number.isInteger(token.end) || token.start < 0 || token.end <= token.start
      || token.end > pageText.length || byStart.has(token.start) || pageText.slice(token.start, token.end) !== token.text
      || ![token.left, token.right, token.top, token.bottom].every(Number.isFinite)
      || token.left >= token.right || token.top >= token.bottom) return empty(words);
    byStart.set(token.start, token);
  }
  if (tokens.some(t => byStart.get(t.index)?.end !== t.index + t[0].length)) return empty(words);
  const rows = groupRows(geometry);
  if (!rows) return empty(words);
  for (const row of rows) {
    const sorted = [...row.tokens].sort((a, b) => a.left - b.left);
    if (sorted.some((t, i) => i && t.left < sorted[i - 1].right - Math.min(t.right - t.left, sorted[i - 1].right - sorted[i - 1].left) * 0.25)) return empty(words);
  }
  const projected = projectArabicGroups(words, language);
  const candidates = orderedCandidates(rows, language).map(tokens => {
    const ranges = projectedRanges(pageText, words, tokens, projected);
    return { ranges, score: ranges.reduce((sum, r) => sum + (r
      ? (r.segments || [r]).reduce((count, s) => count + normalized(pageText.slice(s.start, s.end)).length, 0) : 0), 0) };
  });
  const best = Math.max(...candidates.map(c => c.score));
  const winners = candidates.filter(c => c.score === best);
  // Equally supported layouts must agree on the actual occurrence, not merely
  // its spelling. Ambiguous repeated words are intentionally left unmarked.
  return winners[0].ranges.map((range, i) => winners.every(c => JSON.stringify(c.ranges[i]) === JSON.stringify(range)) ? range : null);
}

function quadBounds(quads) {
  if (!Array.isArray(quads) || !quads.length) return null;
  const x = [], y = [], heights = [];
  for (const q of quads) {
    if (![q.x1, q.x2, q.x3, q.x4, q.y1, q.y2, q.y3, q.y4].every(Number.isFinite)) return null;
    const tolerance = Math.max(0.05, Math.abs(q.y1 - q.y3) * 0.02);
    if (Math.abs(q.y1 - q.y2) > tolerance || Math.abs(q.y3 - q.y4) > tolerance
      || Math.abs(q.x1 - q.x4) > tolerance || Math.abs(q.x2 - q.x3) > tolerance) return null;
    x.push(q.x1, q.x2, q.x3, q.x4); y.push(q.y1, q.y2, q.y3, q.y4);
    heights.push(Math.max(q.y1, q.y2, q.y3, q.y4) - Math.min(q.y1, q.y2, q.y3, q.y4));
  }
  const bounds = { left: Math.min(...x), right: Math.max(...x), top: Math.min(...y), bottom: Math.max(...y) };
  if (bounds.left >= bounds.right || bounds.top >= bounds.bottom || bounds.bottom - bounds.top > Math.max(...heights) * 1.7) return null;
  return bounds;
}

/** Call only for Office sources. A failed geometry lookup never prevents audio. */
export async function alignOfficeSpeechWordsToPageText({ document, pageNumber, pageText, words,
  language, signal, isCurrent = () => true }) {
  if (!Array.isArray(words)) return [];
  const current = () => !signal?.aborted && isCurrent();
  if (!current()) throw abortError();
  if (!document?.getTextPosition || !Number.isSafeInteger(pageNumber) || pageNumber < 1
    || typeof pageText !== 'string' || pageText.length > MAX_CHARACTERS) return empty(words);
  const tokens = [...pageText.matchAll(/\S+/gu)];
  if (!tokens.length || tokens.length > MAX_TOKENS) return empty(words);
  const prior = cache.get(document)?.get(pageNumber);
  if (prior?.text === pageText) return alignOfficeSpeechWordsToGeometry(pageText, words, prior.tokens, { language });
  let stopped = false, cursor = 0, timer, onAbort;
  const geometry = new Array(tokens.length);
  const interrupted = new Promise((_, reject) => {
    timer = setTimeout(() => { stopped = true; reject(new Error('Office geometry timed out')); }, PAGE_TIMEOUT_MS);
    onAbort = () => { stopped = true; reject(abortError()); };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
  try {
    const workers = Array.from({ length: Math.min(8, tokens.length) }, async () => {
      while (!stopped && cursor < tokens.length) {
        if (!current()) throw abortError();
        const i = cursor++, token = tokens[i];
        const quads = await document.getTextPosition(pageNumber, token.index, token.index + token[0].length);
        if (!current()) throw abortError();
        if (stopped) return;
        const bounds = quadBounds(quads);
        if (!bounds) throw new Error('Office word geometry unavailable');
        geometry[i] = { text: token[0], start: token.index, end: token.index + token[0].length, ...bounds };
      }
    });
    await Promise.race([Promise.all(workers), interrupted]);
    if (!current()) throw abortError();
    let pages = cache.get(document);
    if (!pages) { pages = new Map(); cache.set(document, pages); }
    pages.set(pageNumber, { text: pageText, tokens: geometry });
    if (pages.size > 12) pages.delete(pages.keys().next().value);
    return alignOfficeSpeechWordsToGeometry(pageText, words, geometry, { language });
  } catch (error) {
    if (!current() || error?.name === 'AbortError') throw abortError();
    return empty(words);
  } finally {
    stopped = true; clearTimeout(timer); signal?.removeEventListener('abort', onAbort);
  }
}
